<template lang="pug">
.pa-4(:style="cardStyles")
  CommentBlock(
    :viewContext="viewContext"
    v-if="comment",
    :creator="comment.creator",
    :created-at="comment.created_at",
    :text="commentText",
    :menu-items="permittedMenuItems",
    :is-edit="isEdit"
  )
  CommentForm(
    v-if="isEdit",
    :viewContext="viewContext",
    :value="commentText",
    @save="saveComment",
    @cancel="cancelComment",
  )
</template>

<script lang="ts">
import THEME from "../../constants/theme"
import { ref, Ref, computed, defineComponent, watch, PropType } from "vue"
import { Comment, Comment as IComment } from "suus-api"
import { MenuItem } from "interfaces/menuItem.interface"
import CommentBlock from "./CommentBlock.vue"
import CommentForm from "./CommentForm.vue"
import CommentLoading from "./CommentLoading.vue"
import { mdiPencilOutline } from "@mdi/js"
import { mdiDeleteOutline } from "@mdi/js"
import { ViewContext } from "concerns/useContextName"
import { useConfirmationService } from "features/common/confirmation-dialog/confirmation.service"

export default defineComponent({
  name: "Comment",
  components: { CommentBlock, CommentForm, CommentLoading },
  props: {
    comment: {
      type: Object as PropType<IComment>,
      required: true,
    },
    viewContext: { type: Object as PropType<ViewContext> },
  },
  setup(props, context) {
    const initialCommentText: Ref<string> = ref(props.comment.text)
    const commentText: Ref<string> = ref(props.comment.text)
    const isEdit: Ref<boolean> = ref(false)

    function saveComment(val: string): void {
      commentText.value = val
      const updatedComment: Comment = {
        ...props.comment,
        text: commentText.value,
      }
      context.emit("save", updatedComment)
      isEdit.value = false
    }
    function cancelComment(): void {
      commentText.value = initialCommentText.value
      isEdit.value = false
    }

    const confirmationService = useConfirmationService()

    const menuItems: Ref<MenuItem[]> = computed(() => {
      return [
        {
          title: "Bearbeiten",
          key: "edit",
          clickHandler: () => (isEdit.value = true),
          icon: mdiPencilOutline,
          permitted: !!props.comment.links.update_url,
        },
        {
          title: "Löschen",
          key: "delete",
          clickHandler: () => {
            confirmationService.open({
              title: "Kommentar löschen",
              text: "Möchtest du diesen Kommentar wirklich löschen?",
              confirmText: "Löschen",
              cancelText: "Abbrechen",
              onConfirm: () => {
                context.emit("delete", props.comment.id)
              },
            })
          },
          icon: mdiDeleteOutline,
          permitted: !!props.comment.links.delete_url,
        },
      ]
    })

    const permittedMenuItems: Ref<MenuItem[]> = computed(() =>
      menuItems.value.filter((item) => item.permitted)
    )

    const cardStyles = {
      borderRadius: "4px",
      border: `1px solid ${THEME.colors.light.mediumGrey}`,
      backgroundColor: THEME.colors.light.lightGrey,
    }

    watch(
      () => props.comment,
      (newVal) => {
        commentText.value = newVal.text
      }
    )

    return {
      isEdit,
      commentText,
      saveComment,
      cancelComment,
      permittedMenuItems,
      cardStyles,
    }
  },
})
</script>
